import "./style.css";

import { useQueryClient } from "@tanstack/react-query";
import { CirclePlus } from "lucide-react";
import { observer } from "mobx-react-lite";
import { type FC, useId, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  type BrandId,
  useDeleteBrandMutation,
  useUpdateBrandMutation,
} from "@/api/admin/brands";
import {
  type BrandUnderGroup,
  type IBrandUnderGroupPayload,
  useGetGroupByIdQuery,
  useUpdateGroupMutation,
} from "@/api/admin/groups";
import { AdminSections } from "@/api/admin/teamRoles";
import { QUERY_KEYS } from "@/api/constants";
import {
  ActionButtons,
  CustomButton,
  DetailsBlock,
  DetailsList,
  Loader,
} from "@/components/atoms";
import {
  BrandFieldEdit,
  toastError,
  toastSuccess,
} from "@/components/molecules";
import { PATHS } from "@/config/path";
import { cn } from "@/lib/utils";
import {
  BRAND_WIZARD_RETURN_TO_KEY,
  setWizardReturnTo,
} from "@/modules/wizardReturnTo";
import { usePermission } from "@/services/casl/usePermission";
import { commonIconProps20 } from "@/utils/constants";
import { FIELDS_MAPPING } from "@/utils/constants/allFieldsMapping";

import { EntityHistory } from "../EntityHistory";
import {
  GroupInformationForm,
  type GroupInformationFormType,
} from "../GroupInformationForm";

interface AdminGroupDetailsProps {
  data: IBrandUnderGroupPayload;
  hasUpdatePermission?: boolean;
}

const AdminGroupDetails: FC<AdminGroupDetailsProps> = observer(
  ({ data, hasUpdatePermission = true }) => {
    const { id = "" } = useParams();

    const navigate = useNavigate();
    const [isActive, setActive] = useState(false);

    const queryClient = useQueryClient();
    const formID = useId();
    const brands = data.brandsWithBranches;

    const {
      detailsList: groupDetailsList,
      data: groupData,
      isPending: isGroupDetailsPending,
    } = useGetGroupByIdQuery(id);

    const { mutate: updateGroup, isPending: isUpdateGroupPending } =
      useUpdateGroupMutation();

    const { mutate: deleteBrand, isPending: isDeletePending } =
      useDeleteBrandMutation();

    const { mutate: updateBrand, isPending: isUpdatePending } =
      useUpdateBrandMutation();

    const handleEdit = async (brandId: BrandId) => {
      setWizardReturnTo(
        BRAND_WIZARD_RETURN_TO_KEY,
        PATHS.ADMIN.ACCOUNT_GROUP_DETAILS(id)
      );
      navigate(PATHS.ADMIN.BRAND_WIZARD_DETAILS(brandId as string, id));
    };

    const handleSubmit = (data: GroupInformationFormType) => {
      updateGroup(
        { id, data },
        {
          onSuccess: () => {
            toastSuccess("Group was updated successfully!");
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.group, id],
            });
            setActive(false);
          },
          onError: error => {
            toastError(error.message, () => handleSubmit(data), "Error");
          },
        }
      );
      return data;
    };

    const handleRemoveBrand = (brand: BrandUnderGroup) => {
      if (brand.active) {
        deleteBrand(brand.brandId, {
          onSuccess: () => {
            toastSuccess("Brand was deactivated successfully!");
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.group, id],
            });
          },
          onError: () => {
            toastError(
              "Oops! Something went wrong.",
              () => handleRemoveBrand(brand),
              "Error"
            );
          },
        });
      } else {
        updateBrand(
          {
            id: brand.brandId,
            data: {
              ...brand,
              active: true,
            },
          },
          {
            onSuccess: () => {
              toastSuccess("Brand was activated successfully!");
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.group, id],
              });
            },
            onError: () => {
              toastError(
                "Oops! Something went wrong.",
                () => handleRemoveBrand(brand),
                "Error"
              );
            },
          }
        );
      }
    };

    const hasBrandPermission = usePermission(AdminSections.BRAND);

    if (isGroupDetailsPending || isUpdateGroupPending) {
      return <Loader fullscreen />;
    }

    return (
      <div className="pt-6 flex gap-6 w-full flex-col md:flex-row">
        <div className="w-full md:w-[62%] flex flex-col gap-[16px]">
          <DetailsBlock
            title="Details"
            isEdit={hasUpdatePermission}
            isActive={isActive}
            setActive={setActive}
          >
            <>
              {isActive ? (
                <div className="flex flex-col gap-[3px]">
                  <GroupInformationForm
                    defaultValues={groupData as GroupInformationFormType}
                    onSubmit={handleSubmit}
                    id={formID}
                    classes="px-0 pb-0"
                    isWide
                    isEditable
                  />
                  <ActionButtons
                    formID={formID}
                    click={() => setActive(false)}
                    isPending={isUpdateGroupPending}
                  />
                </div>
              ) : (
                <DetailsList data={groupDetailsList} />
              )}
              <EntityHistory
                entityId={id}
                entityName={data.name}
                blockName="Details"
                wrapperClass="absolute right-13 top-4.5"
                mapKeyToName={FIELDS_MAPPING.groupDetails}
              />
            </>
          </DetailsBlock>

          <DetailsBlock
            title="Brands under group"
            classes="sticky top-0 bg-white z-10 gap-0 max-h-[540px]"
          >
            <>
              {brands.length > 0 && (
                <ul
                  className="border border-gray-200 rounded-t-[6px] text-sm bg-white mt-[24px] 
                 overflow-y-scroll flex-1"
                >
                  {brands.map(brand => (
                    <BrandFieldEdit
                      key={brand.brandId}
                      name={brand.brandName}
                      list={brand.branches}
                      isTrash
                      classes="pr-0"
                      editClik={() => handleEdit(brand.brandId)}
                      removeClick={() => handleRemoveBrand(brand)}
                      state="brand"
                      isPending={isDeletePending || isUpdatePending}
                      isActive={brand.active}
                      linkToEntity={
                        hasBrandPermission
                          ? PATHS.ADMIN.ACCOUNT_BRAND_DETAILS(brand.brandId)
                          : ""
                      }
                      hasUpdatePermission={hasUpdatePermission}
                      disabled={!hasUpdatePermission || !groupData?.active}
                    />
                  ))}
                </ul>
              )}

              {hasUpdatePermission && (
                <div
                  className={cn(
                    "relative border border-gray-200 pr-4 pl-3 py-[6px] bg-gray-50",
                    brands.length > 0
                      ? "border-t-0 rounded-b-[8px]"
                      : "border rounded-[8px] mt-6"
                  )}
                >
                  <CustomButton
                    variant="ghost"
                    type="button"
                    onClick={() => {
                      setWizardReturnTo(
                        BRAND_WIZARD_RETURN_TO_KEY,
                        PATHS.ADMIN.ACCOUNT_GROUP_DETAILS(id)
                      );
                      navigate(PATHS.ADMIN.BRAND_WIZARD_DETAILS(undefined, id));
                    }}
                    data-testid="admin-group-add-brand-button"
                  >
                    <CirclePlus {...commonIconProps20} />
                    Add new brand
                  </CustomButton>
                </div>
              )}
            </>
          </DetailsBlock>
        </div>
        {/* TODO:hidden until full integration */}
        {/* <DetailsBlock
          classes={cn(
            "w-full md:w-[37%] p-[32px] pb-0 h-fit gap-0!",
            isDevelope && "border-[#DC2626] border-2"
          )}
        >
          <DetailsOverviewToday
            title="Brands overview"
            positions={positions}
            classes="p-0"
          />
        </DetailsBlock> */}
      </div>
    );
  }
);

export default AdminGroupDetails;
